import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import axios from "axios"
import { useParams, useSearchParams } from "react-router-dom"
import { Smartphone, Activity, Share2, ChevronLeft, ChevronRight } from "lucide-react"
import SEO from "../../components/Common/SEO"
import { okara_api } from "../../utils/variables"
import { REPORT_COLORS, REPORT_RADIUS } from "./components/reportTheme"
import { ProcessingState, DisabledState, NotFoundState, FailedState } from "./components/PublicReportStates"
import { isBlurredField, blurAware, BlurredInline, BlurredContentFade, BlurUpgradeContext } from "./components/BlurredField"
import AppsTrackingPlanModal from "../../components/Common/Modal/AppsTrackingPlanModal"
import AuthModal from "../../components/Common/Modal/AuthModal"
import { configureToastr, triggerToast } from "../../utils/toast"
import PublicAICMOTab from "./publicTabs/PublicAICMOTab"
import PublicForecastTab from "./publicTabs/PublicForecastTab"
import PublicSocialTab from "./publicTabs/PublicSocialTab"

// The standalone AI Reports tab was retired — its performance-report data
// (downloads/revenue estimates, metrics, sentiment, key observations,
// recommendations) all comes from the same `report`/`load` sections AI CMO
// already reads, so it's folded into that tab instead of shown twice.
const TABS = [
  { key: "ai-cmo", label: "AI CMO", icon: Smartphone },
  { key: "social", label: "Social Comparison", icon: Share2 },
  { key: "forecasting", label: "AI Forecasting", icon: Activity },
]

// The public build can take ~2 minutes for a fully cold app (ads, marketing
// intelligence and SEO generated from scratch) — poll generously rather than
// giving up early.
const POLL_INTERVAL_MS = 5000
const MAX_POLL_ATTEMPTS = 60

// Recursively checks whether any field anywhere in the report sections
// carries the backend's {blurred: true, ...} descriptor (see isBlurredField
// in BlurredField.jsx). There's no separate plan/entitlement flag available
// client-side without another API call, so "some field is blurred" is the
// free-vs-paid signal used for the proactive upgrade nudge below.
const containsBlurredField = (value, depth = 0) => {
  if (depth > 8) return false
  if (isBlurredField(value)) return true
  if (Array.isArray(value)) return value.some(v => containsBlurredField(v, depth + 1))
  if (value && typeof value === "object") return Object.values(value).some(v => containsBlurredField(v, depth + 1))
  return false
}

const AUTO_UPGRADE_DELAY_MS = 30000
const AI_CMO_UPGRADE_DELAY_MS = 10000

// After returning from Stripe checkout the backend may not have processed
// the payment webhook yet, so the first fetch can still come back blurred.
const POST_UPGRADE_RETRY_MS = 3000
const POST_UPGRADE_MAX_RETRIES = 5

// "anonymous" (no token), "guest" ("continue as guest" — a real token whose
// authUser carries is_guest, same check as AppsTrackingPlanModal's
// isGuestOrLoggedOut), or "user". Only a real user can buy a plan, so
// anything else is asked to sign in / sign up first.
const getViewerAuthState = () => {
  if (!localStorage.getItem("authToken")) return "anonymous"
  try {
    const authUser = JSON.parse(localStorage.getItem("authUser"))
    if (authUser?.data?.is_guest === true || authUser?.is_guest === true) return "guest"
  } catch (e) {
    /* treat unparsable authUser like a normal token */
  }
  return "user"
}

// A logged-in visitor's own auth token tells the backend who they are and
// (via their plan) whether they're entitled to the unblurred report — same
// Authorization convention used by every other authenticated call in the
// app (e.g. AppsTrackingPlanModal).
const requestReport = slug => {
  const authToken = localStorage.getItem("authToken")
  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : undefined
  return axios.get(`${okara_api}/public/report/${slug}/`, { headers, validateStatus: () => true })
}

// TEMPORARY — visual QA only for the consolidated blur/unlock redesign.
// Navigate to /report/__mock__ to render this instead of hitting the API.
// REMOVE this const and its short-circuit in the effect below before commit.
const MOCK_REPORT = {
  app: { name: "Velocity Racer VR", image_url: "https://picsum.photos/id/1071/64", developer: "Mock Studios", platform: "Meta Quest" },
  sections: {
    load: {
      available: true,
      data: {
        game_info: {
          name: "Velocity Racer VR",
          image_url: "https://picsum.photos/id/1071/120",
          rating: { average: 4.5, total_rating: 128, rating_index: { 5: 90, 4: 20, 3: 8, 2: 6, 1: 4 } },
          category: "Games",
          genre: ["Racing", "Simulation"],
          price: { currency: "USD", amount: 0 },
          publisher: "Mock Studios",
          age_rating: "Everyone",
          website_url: "https://example.com",
          description: "Race through realistic tracks in VR with physics-based driving mechanics.",
          keywords: ["racing", "vr", "car", "arcade"],
          keyword_stats: {},
          analytics: { d: 125000, revenue: 45000, urv: 3200, ku: 1.42, asr: 12.3, ktr: 0.09, aur: 3.1 },
          screenshots: [
            "https://picsum.photos/id/1011/300/200",
            "https://picsum.photos/id/1015/300/200",
            "https://picsum.photos/id/1016/300/200",
            { blurred: true, message: "5 more screenshots on the full report" },
            { blurred: true, message: "5 more screenshots on the full report" },
            { blurred: true, message: "5 more screenshots on the full report" },
            { blurred: true, message: "5 more screenshots on the full report" },
            { blurred: true, message: "5 more screenshots on the full report" },
          ],
          reviews: [
            { title: "Great physics!", review: "Really fun racing mechanics.", upvotes: 12 },
            { title: "Needs more tracks", review: "Would love more content.", upvotes: 4 },
          ],
          similar_apps: [
            { item_id: "c1", name: "VRFS - Football Sim", image_url: "https://picsum.photos/id/1035/64", rating: 4.8, review_count: 43000, reasons: ["Shares the sports genre tag."], url: "https://example.com/c1" },
            { item_id: "c2", name: "Derby Baseball", image_url: "https://picsum.photos/id/1041/64", rating: 4.9, review_count: 34100, reasons: ["Large established user base."], url: "https://example.com/c2" },
            { blurred: true, message: "3 more competitors on the full report" },
            { blurred: true, message: "3 more competitors on the full report" },
            { blurred: true, message: "3 more competitors on the full report" },
          ],
        },
        policy: {
          policy_report: {
            summary: { compliance_status: "Compliant", confidence: 0.95 },
            blocking_issues: [
              { title: "Missing privacy policy link", description: "Store listing has no privacy policy URL.", severity: "high" },
              { blurred: true, message: "2 more blocking issues on the full report" },
              { blurred: true, message: "2 more blocking issues on the full report" },
            ],
            recommended_improvements: [
              { title: "Add more screenshots", suggested_improvement: "Show gameplay variety." },
              { blurred: true, message: "3 more recommendations on the full report" },
            ],
            store_page_gaps: [],
            submission_checklist: { icon: "provided", screenshots: "provided", privacy_policy: "missing" },
          },
        },
        sentiment: {
          overall_sentiment: "Mixed",
          data_confidence: "High",
          overall_summary: "The sentiment is mixed due to a combination of positive and negative feedback.",
          sentiment_breakdown: { positive_percent: 55, neutral_percent: 5, negative_percent: 40 },
          top_positive_themes: [
            "Fun, realistic racing mechanics",
            { blurred: true, message: "1 more positive theme on the full report" },
          ],
          top_negative_themes: [{ blurred: true, message: "2 more negative themes on the full report" }],
          bug_complaints: [{ blurred: true, message: "1 bug complaint on the full report" }],
        },
        actions_feed: {
          summary: { problems: 1, advantages: 2 },
          feed: [
            { blurred: true, message: "Locked action — available on the full report" },
            { title: "Healthy cumulative download base", description: "Downloads are steadily growing month over month." },
            { title: "Revenue traction established", description: "Revenue is trending positively." },
            { blurred: true, message: "Locked action — available on the full report" },
          ],
        },
        report: {
          response: {
            key_observations: [
              { title: "Significant download increase", explanation: { blurred: true, message: "Upgrade to see download and revenue estimates." } },
              { title: "Mixed sentiment with moderation issues", explanation: "No measurable relationship detected between sentiment and metrics" },
              { title: "Bug Complaints & App Stability", explanation: "Bug Complaints: Gameplay Bugs" },
            ],
            recommendations: {
              bug_fix_plan: ["Improve voice chat moderation to address unfair bans and offensive behavior.", "Fix gameplay bugs such as the ball phasing through players."],
              keyword_tracking_monitoring: ["Weekly KU tracking", "KTR monitoring"],
              keyword_expansion: [],
            },
          },
        },
      },
    },
    social_accounts: {
      available: true,
      data: {
        summary: { total_followers: 45000, accounts_count: 3 },
        accounts: [
          { platform: "Instagram", handle: "velocityracer", counts: { followers: 30000 }, url: "https://instagram.com/x", verified: true },
          { blurred: true, message: "2 more accounts on the full report" },
          { blurred: true, message: "2 more accounts on the full report" },
        ],
        other_links: [],
      },
    },
    seo: {
      available: true,
      data: {
        seo_score: 7,
        max_score: 10,
        competitors_analyzed: 5,
        keywords_used: ["racing", "vr", "car"],
        missing_keywords: ["car simulator", "vr arcade"],
        optimization_suggestions: [
          "Add more keywords to the title.",
          { blurred: true, message: "2 more suggestions on the full report" },
        ],
        content_gaps: [],
        keyword_frequency: { racing: 12, vr: 9, car: 5 },
      },
    },
    ad_intelligence: {
      available: true,
      data: {
        ads: {
          ads: [
            { _id: "a1", headline: "Race Now!", cta: "Install", image_urls: ["https://picsum.photos/id/1040/300/200"], is_active: true },
            { blurred: true, message: "2 more ads on the full report" },
            { blurred: true, message: "2 more ads on the full report" },
          ],
          active_ad_count: 2,
          ad_count: 5,
          platforms: ["Facebook", "Instagram"],
          ad_types: ["Video"],
          total_impressions_est: 50000,
          verified: true,
        },
        competitor_ads: null,
      },
    },
    marketing_intelligence: {
      available: true,
      data: {
        score: 72,
        score_why: "Based on ad spend, social presence and SEO signals.",
        summary: "Strong social presence with room to grow on TikTok.",
        key_findings: ["Instagram is the strongest channel.", { blurred: true, message: "1 more finding on the full report" }],
        social: {
          platforms: [
            { platform: "instagram", priority: "high", read: "Best performing channel.", action: "Post daily." },
            { blurred: true, message: "1 more platform play on the full report" },
          ],
          account_fixes: [],
          caption_and_ad_keywords: [],
          verdict: "Strong presence on Instagram, weak on TikTok.",
          best_platform: "instagram",
          weakest_platform: "tiktok",
        },
        competitors: [
          { name: "Rival Racer", threat: "high", why: "Larger ad budget.", exploit: "Target their weak reviews." },
          { blurred: true, message: "1 more threat on the full report" },
        ],
        actions: [{ do: "Increase posting frequency.", priority: "high" }],
        content_plan: [],
        position: "Leader",
      },
    },
    platform_intelligence: {
      available: true,
      data: { platform_counts: { instagram: 5, tiktok: 3 }, platforms: {} },
    },
    forecast: {
      available: true,
      data: {
        recent_downloads: [{ date: "2026-08-01", value: 100 }, { date: "2026-08-02", value: 110 }],
        forecast: [{ date: "2026-08-03", downloads: 120 }],
        recent_revenue: [],
        revenue_forecast: [],
        suggestions: ["Increase ad spend on Instagram.", { blurred: true, message: "1 more suggestion on the full report" }],
        insights: ["Downloads are trending up.", { blurred: true, message: "1 more insight on the full report" }],
        growth_status: "Growing",
        uplift: 15,
      },
    },
  },
}

export default function PublicReport() {
  const { slug } = useParams()
  const [status, setStatus] = useState("loading") // loading | processing | disabled | notfound | failed | ready
  const [report, setReport] = useState(null)
  const [activeTab, setActiveTab] = useState(TABS[0].key)
  const [showPlanModal, setShowPlanModal] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [viewerAuthState, setViewerAuthState] = useState(getViewerAuthState)
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTabIndex = TABS.findIndex(t => t.key === activeTab)

  // Next/Previous (and the tab bar itself) step through the four tabs in
  // order. Jump back to the very top of the page on every change — skipping
  // the very first render, so landing on the page doesn't itself trigger a
  // scroll — since otherwise switching tabs leaves the reader wherever
  // they'd scrolled to in the *previous* tab. This used to scrollIntoView
  // just the tab content div, which pins that div's own top to the viewport
  // top — pushing the app header and tab bar above it out of view instead
  // of landing the reader at the actual top of the page.
  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      return
    }
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [activeTab])

  // Every Unlock click lands here (see BlurUpgradeContext in
  // BlurredField.jsx). A logged-out or guest visitor can't buy a plan, so
  // they get the in-place sign in / sign up modal first; once they're a
  // real user, handleAuthSuccess re-fetches the report with their token and
  // only shows the plans modal if their plan still leaves content locked.
  // Login state is re-read live rather than from state, so a sign-out in
  // another tab is respected.
  const requestUnlock = () => {
    if (getViewerAuthState() !== "user") {
      setShowAuthModal(true)
      return
    }
    setShowPlanModal(true)
  }
  const blurUpgradeContextValue = { openUpgradeModal: requestUnlock }
  const isLoggedIn = viewerAuthState === "user"

  // On small screens the tab bar scrolls horizontally. The native scrollbar
  // is unreliable as a hint: on macOS/mobile it's an overlay that auto-hides
  // after a moment of inactivity regardless of any color/width CSS, so a
  // user who pauses sees no scrollbar at all. Track scroll position in React
  // state instead and render our own persistent track + thumb, plus a
  // fading edge, so the affordance never disappears on its own.
  const tabsScrollRef = useRef(null)
  const [tabsScroll, setTabsScroll] = useState({ hasOverflow: false, thumbPct: 0, leftPct: 0, atEnd: false })

  useEffect(() => {
    const el = tabsScrollRef.current
    if (!el) return
    const update = () => {
      const { scrollWidth, clientWidth, scrollLeft } = el
      const hasOverflow = scrollWidth - clientWidth > 4
      const maxScroll = scrollWidth - clientWidth
      setTabsScroll({
        hasOverflow,
        thumbPct: hasOverflow ? (clientWidth / scrollWidth) * 100 : 0,
        leftPct: hasOverflow && maxScroll > 0 ? (scrollLeft / maxScroll) * (100 - (clientWidth / scrollWidth) * 100) : 0,
        atEnd: !hasOverflow || maxScroll - scrollLeft <= 4,
      })
    }
    update()
    el.addEventListener("scroll", update, { passive: true })
    window.addEventListener("resize", update)
    return () => {
      el.removeEventListener("scroll", update)
      window.removeEventListener("resize", update)
    }
  }, [status])

  useEffect(() => {
    if (!slug) return
    // TEMPORARY — visual QA short-circuit, remove before commit.
    if (slug === "__mock__") {
      setReport(MOCK_REPORT)
      setStatus("ready")
      return
    }
    let cancelled = false
    let timer = null
    let attempts = 0

    const fetchOnce = async () => {
      let res
      try {
        res = await requestReport(slug)
      } catch (err) {
        if (!cancelled) setStatus("notfound")
        return
      }
      if (cancelled) return

      if (res.status === 200) {
        setReport(res.data)
        setStatus("ready")
        return
      }
      if (res.status === 202) {
        attempts += 1
        if (attempts > MAX_POLL_ATTEMPTS) {
          setStatus("failed")
          return
        }
        setStatus("processing")
        timer = setTimeout(fetchOnce, POLL_INTERVAL_MS)
        return
      }
      if (res.status === 403) {
        setStatus("disabled")
        return
      }
      if (res.status === 503) {
        setStatus("failed")
        return
      }
      setStatus("notfound")
    }

    fetchOnce()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [slug])

  // One-off re-fetch of an already-loaded report (e.g. after signing in or
  // upgrading) — unlike the initial load above it never swaps the page for
  // a loading/processing state, it just replaces the data if it comes back.
  // Returns the fresh report, or null if it didn't come back ready.
  const refreshReport = useCallback(async () => {
    if (!slug || slug === "__mock__") return null
    try {
      const res = await requestReport(slug)
      if (res.status === 200) {
        setReport(res.data)
        return res.data
      }
    } catch (e) {
      /* keep showing the current report */
    }
    return null
  }, [slug])

  const app = report?.app
  const sections = report?.sections || {}

  // The proactive (un-clicked) auto-upgrade nudge below only fires for a
  // real logged-in free user who's stuck around — a logged-out/guest visitor
  // still gets the sign-in modal the moment they click an Unlock affordance
  // themselves, just not this unprompted timed nudge.
  const hasBlurredContent = useMemo(() => containsBlurredField(report?.sections), [report])
  const isFreeUser = isLoggedIn && hasBlurredContent

  // Proactively nudge a free user who hasn't clicked anything themselves:
  // once after ~1.5 minutes on the report, or sooner if they land on AI CMO
  // (the most-locked tab) and stay 10s — whichever happens first. Never
  // more than once per visit.
  const autoUpgradeShownRef = useRef(false)

  useEffect(() => {
    if (!isFreeUser) return
    const timer = setTimeout(() => {
      if (autoUpgradeShownRef.current) return
      autoUpgradeShownRef.current = true
      setShowPlanModal(true)
    }, AUTO_UPGRADE_DELAY_MS)
    return () => clearTimeout(timer)
  }, [isFreeUser])

  useEffect(() => {
    if (!isFreeUser || activeTab !== "ai-cmo") return
    const timer = setTimeout(() => {
      if (autoUpgradeShownRef.current) return
      autoUpgradeShownRef.current = true
      setShowPlanModal(true)
    }, AI_CMO_UPGRADE_DELAY_MS)
    return () => clearTimeout(timer)
  }, [isFreeUser, activeTab])

  // Signed in / signed up from the Unlock modal: re-fetch with the new token
  // (their plan may already unlock everything), then continue to the plans
  // modal only if something is still locked. Either way they stay on this
  // report.
  const handleAuthSuccess = async () => {
    setShowAuthModal(false)
    setViewerAuthState(getViewerAuthState())
    autoUpgradeShownRef.current = true
    const fresh = await refreshReport()
    if (containsBlurredField((fresh || report)?.sections)) {
      setShowPlanModal(true)
    } else {
      try { configureToastr() } catch (e) { /* ignore */ }
      triggerToast("You’re signed in — the full report is unlocked.", "success")
    }
  }

  // In-place paid-to-paid upgrade inside the plans modal (no Stripe
  // redirect): close it and re-fetch so newly unlocked sections appear.
  const handlePlanChanged = async () => {
    setShowPlanModal(false)
    await refreshReport()
  }

  // Arrival flags:
  //  - `upgraded=1`: back from Stripe checkout (PaymentSuccess). Re-check a
  //    few times for the unlocked report, since the payment webhook can lag
  //    the redirect.
  //  - `unlock=1`: back from the email-verification -> /login path of a
  //    sign-up started here (see AuthModal's returnPath). Continue straight
  //    to the plans modal if their plan doesn't cover the report.
  // Both are stripped from the URL once handled so a refresh doesn't repeat.
  const arrivalHandledRef = useRef(false)
  useEffect(() => {
    if (status !== "ready" || arrivalHandledRef.current) return
    const upgraded = searchParams.get("upgraded") === "1"
    const unlock = searchParams.get("unlock") === "1"
    if (!upgraded && !unlock) return
    arrivalHandledRef.current = true
    autoUpgradeShownRef.current = true

    const clearFlags = () => {
      const next = new URLSearchParams(searchParams)
      next.delete("upgraded")
      next.delete("unlock")
      setSearchParams(next, { replace: true })
    }

    if (unlock) {
      clearFlags()
      if (hasBlurredContent) requestUnlock()
      return
    }

    let cancelled = false
    ;(async () => {
      let stillLocked = hasBlurredContent
      for (let i = 0; stillLocked && i < POST_UPGRADE_MAX_RETRIES && !cancelled; i++) {
        await new Promise(resolve => setTimeout(resolve, POST_UPGRADE_RETRY_MS))
        if (cancelled) return
        const fresh = await refreshReport()
        if (fresh) stillLocked = containsBlurredField(fresh.sections)
      }
      if (cancelled) return
      clearFlags()
      try { configureToastr() } catch (e) { /* ignore */ }
      if (stillLocked) {
        triggerToast("Your plan is active. Some sections may take a moment to unlock — refresh shortly if they're still hidden.", "info")
      } else {
        triggerToast("Your plan is active — the full report is unlocked.", "success")
      }
    })()
    return () => {
      cancelled = true
      // Let a StrictMode/dev re-run of this effect pick the work back up.
      arrivalHandledRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  // The app's own name/image can themselves be admin-blurred fields — never
  // pass the raw (possibly-blurred) value down as the `appName` fallback
  // prop, or a child tab's own "value || appName" fallback would render the
  // blurred descriptor object as a React child and crash.
  const appNameBlurred = isBlurredField(app?.name) ? app.name : null
  const appName = typeof app?.name === "string" ? app.name : null
  const appImageUrl = typeof app?.image_url === "string" ? app.image_url : null
  const appImageBlurred = isBlurredField(app?.image_url) ? app.image_url : null

  return (
    <div style={{ minHeight: "100dvh" }}>
      <div className="container-fluid" style={{ padding: "clamp(20px, 6vw, 32px) clamp(12px, 4vw, 16px)", maxWidth: 1100, margin: "0 auto" }}>
        <SEO
          title={appName ? `${appName} — App Report | KeyQuest` : "App Report | KeyQuest"}
          description="Shared AI CMO, forecasting, SEO and social presence report."
          robots="noindex, nofollow"
        />

        {status === "loading" && <ProcessingState />}
        {status === "processing" && <ProcessingState />}
        {status === "disabled" && <DisabledState />}
        {status === "notfound" && <NotFoundState />}
        {status === "failed" && <FailedState />}

        {status === "ready" && report && (
          <BlurUpgradeContext.Provider value={blurUpgradeContextValue}>
            <div className="mb-4">
              <div className="d-flex align-items-center justify-content-between" style={{ gap: 14, flexWrap: "wrap" }}>
                <div className="d-flex align-items-center" style={{ gap: 14, minWidth: 0 }}>
                  {appImageBlurred ? (
                    <BlurredInline field={appImageBlurred} interactive={false} />
                  ) : (
                    appImageUrl && (
                      <img
                        src={appImageUrl}
                        alt={appName || "App icon"}
                        style={{ width: "clamp(40px, 12vw, 48px)", height: "clamp(40px, 12vw, 48px)", borderRadius: 12, objectFit: "cover", flexShrink: 0 }}
                      />
                    )
                  )}
                  <div style={{ minWidth: 0 }}>
                    <h1 style={{ color: REPORT_COLORS.textPrimary, fontSize: "clamp(18px, 5.5vw, 22px)", fontWeight: 700, margin: 0 }}>
                      {appNameBlurred ? <BlurredInline field={appNameBlurred} interactive={false} /> : appName || "App Report"}
                    </h1>
                    <p style={{ color: REPORT_COLORS.textSecondary, fontSize: 13, margin: "2px 0 0", display: "flex", gap: 6, alignItems: "center" }}>
                      {[app?.developer, app?.platform].filter(v => typeof v === "string" || isBlurredField(v)).map((v, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span>·</span>}
                          {blurAware(v, undefined, undefined, false)}
                        </React.Fragment>
                      ))}
                    </p>
                  </div>
                </div>
                {/* One shared Unlock for a free-tier viewer, sitting next to
                    the title/icon instead of buried in a tab — gated on
                    whether ANYTHING in this report is locked (not just the
                    header's own image/name/developer/platform), so it's
                    there the moment a free viewer lands, not only once they
                    scroll into a locked section. Still just the one button:
                    a fully-unlocked report shows nothing here. */}
                {hasBlurredContent && (
                  <div style={{ flexShrink: 0 }}>
                    <BlurredContentFade items={[]} wrap />
                  </div>
                )}
              </div>
            </div>

            <div style={{ position: "relative", marginBottom: 24, borderRadius: REPORT_RADIUS, overflow: "hidden" }}>
              <style>{`
                .report-tabs-bar::-webkit-scrollbar { display: none; }
              `}</style>
              <div
                ref={tabsScrollRef}
                className="report-tabs-bar"
                style={{
                  display: "flex",
                  width: "100%",
                  backgroundColor: REPORT_COLORS.cardBg,
                  border: `1px solid ${REPORT_COLORS.cardBorder}`,
                  borderRadius: REPORT_RADIUS,
                  padding: 5,
                  gap: 4,
                  overflowX: "auto",
                  scrollbarWidth: "none",
                }}
              >
                {TABS.map(tab => {
                  const isActive = activeTab === tab.key
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      style={{
                        flex: "1 0 auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        padding: "clamp(9px, 3vw, 12px) clamp(10px, 3vw, 16px)",
                        borderRadius: REPORT_RADIUS - 5,
                        border: "none",
                        cursor: "pointer",
                        fontSize: "clamp(12px, 3.2vw, 14px)",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        transition: "background-color 0.2s ease, color 0.2s ease",
                        backgroundColor: isActive ? REPORT_COLORS.accent : "transparent",
                        color: isActive ? "#fff" : REPORT_COLORS.textMuted,
                      }}
                    >
                      <tab.icon size={16} />
                      <span>{tab.label}</span>
                    </button>
                  )
                })}
              </div>
              {tabsScroll.hasOverflow && (
                <>
                  {!tabsScroll.atEnd && (
                    <div
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        top: 1,
                        right: 1,
                        bottom: 1,
                        width: 28,
                        borderRadius: `0 ${REPORT_RADIUS - 1}px ${REPORT_RADIUS - 1}px 0`,
                        background: `linear-gradient(to right, transparent, ${REPORT_COLORS.cardBg})`,
                        pointerEvents: "none",
                      }}
                    />
                  )}
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: 8,
                      right: 8,
                      bottom: 2,
                      height: 3,
                      borderRadius: 3,
                      backgroundColor: REPORT_COLORS.cardBorder,
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: `${tabsScroll.leftPct}%`,
                        width: `${tabsScroll.thumbPct}%`,
                        minWidth: 24,
                        borderRadius: 3,
                        backgroundColor: REPORT_COLORS.accent,
                      }}
                    />
                  </div>
                </>
              )}
            </div>

            <div>
              {activeTab === "ai-cmo" && <PublicAICMOTab loadSection={sections.load} appName={appName} />}
              {activeTab === "social" && (
                <PublicSocialTab
                  socialSection={sections.social_accounts}
                  seoSection={sections.seo}
                  adsSection={sections.ad_intelligence}
                  marketingSection={sections.marketing_intelligence}
                  platformSection={sections.platform_intelligence}
                  appName={appName}
                />
              )}
              {activeTab === "forecasting" && <PublicForecastTab section={sections.forecast} appName={appName} />}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                marginTop: 8,
                paddingTop: 16,
                borderTop: `1px solid ${REPORT_COLORS.cardBorder}`,
              }}
            >
              <button
                type="button"
                onClick={() => setActiveTab(TABS[Math.max(0, activeTabIndex - 1)].key)}
                disabled={activeTabIndex === 0}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: `1px solid ${REPORT_COLORS.cardBorder}`,
                  backgroundColor: "transparent",
                  color: activeTabIndex === 0 ? REPORT_COLORS.textMuted : REPORT_COLORS.textPrimary,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: activeTabIndex === 0 ? "not-allowed" : "pointer",
                  opacity: activeTabIndex === 0 ? 0.5 : 1,
                }}
              >
                <ChevronLeft size={14} /> Previous
              </button>

              <span style={{ fontSize: 11, color: REPORT_COLORS.textMuted, fontWeight: 600, textAlign: "center" }}>
                Tab {activeTabIndex + 1} of {TABS.length}
                <br />
                {TABS[activeTabIndex]?.label}
              </span>

              <button
                type="button"
                onClick={() => setActiveTab(TABS[Math.min(TABS.length - 1, activeTabIndex + 1)].key)}
                disabled={activeTabIndex === TABS.length - 1}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "none",
                  backgroundColor: REPORT_COLORS.accent,
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: activeTabIndex === TABS.length - 1 ? "not-allowed" : "pointer",
                  opacity: activeTabIndex === TABS.length - 1 ? 0.5 : 1,
                }}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>

            {/* Only ever opened for a real signed-in user (see requestUnlock).
                returnPath brings them back to this report after Stripe
                checkout instead of the dashboard. */}
            <AppsTrackingPlanModal
              show={showPlanModal}
              handleClose={() => setShowPlanModal(false)}
              title=" Upgrade your plan to unlock this feature"
              returnPath={`/report/${slug}`}
              onPlanChanged={handlePlanChanged}
            />

            <AuthModal
              show={showAuthModal}
              onHide={() => setShowAuthModal(false)}
              onAuthSuccess={handleAuthSuccess}
              defaultTab="signup"
              title="Unlock the full report"
              subtitle="Create a free account or sign in to see full estimates for this app."
              returnPath={`/report/${slug}?unlock=1`}
            />
          </BlurUpgradeContext.Provider>
        )}
      </div>
    </div>
  )
}
